import React from 'react';
import { UserIcon, CubeIcon, ChatBubbleLeftIcon } from '@heroicons/react/24/outline';
import Row from '../../pro/components/orderform/Row';
import { Label, LabelStacked } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';
import SelectInput from '../../pro/components/orderform/SelectInput';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import Stack from '../../pro/components/orderform/Stack';
import Card from '../components/Card';
import FieldValue from '../components/FieldValue';
import BillToLookupField from '../components/BillToLookupField';
import CustomerLookupField from '../components/CustomerLookupField';
import type { QuoteDetailData, BillToLookupRow, CustomerLookupRow } from '../fixtures';
import { FUEL_CALC_TYPES, RATE_APPLIES, withCurrent } from '../quoteOptions';
import { keyAccountClass } from '../quoteColors';
import './customerInfoTab.css';

interface Props {
  data: QuoteDetailData;
  /**
   * Optional change callback for the editable masterquote/quoterevisions fields.
   * When provided (the page passes it only when the quote is editable), the
   * fields in HEADER_EDITABLE render as inputs and emit `(field, value)` where
   * `field` is the camelCase `QuoteDetailData` key (matching the `value=`
   * source) and `value` is the new string. Every OTHER field — and ALL fields
   * when this callback is ABSENT — renders as a plain `<FieldValue>` (text, not
   * a control) so non-editable fields no longer masquerade as editable.
   *
   * Editable (HEADER_EDITABLE): addr1, addr2, city, stateCode, zip, billFax,
   * contact, phone, fax, email, csr, volume, plus the Category-A
   * fields sfOpp, other, contractBid, fuelCalc, rateApplies, estRev, estMargin.
   *
   * Read-only (→ FieldValue): the Customer NAME, Bill-To NAME and Sage-Tag
   * identity stamps; market, keyAccount, credTerms, credHold, billComments
   * (read-only per V1); A/R balance; the V2 Quote#/Lane lookup; and the
   * Year-over-Year / Revision Statistics stat grids.
   */
  onHeaderChange?: (field: keyof QuoteDetailData, value: string) => void;
  /** Bill-to reconcile (V1 deferred new-bill-to). Provided only when editable.
   *  Typing renames + unlinks (billto → 0, a new bill-to); picking links a real
   *  bill-to recno and re-denormalizes its address onto the quote. */
  onTypeBillTo?: (name: string) => void;
  onPickBillTo?: (row: BillToLookupRow) => void;
  /** Customer typeahead (create mode) — twin of onTypeBillTo/onPickBillTo.
   *  Typing renames + unlinks (customercode → 0); picking links a real customer
   *  and hydrates market / key account / sales rep. Provided only when editable. */
  onTypeCustomer?: (name: string) => void;
  onPickCustomer?: (row: CustomerLookupRow) => void;
  /** Create mode: open the New Bill-To / New Customer modal (with the typed
   *  term). Surfaced as the typeaheads' "+ Add As New …" action. */
  onAddNewBillTo?: (term: string) => void;
  onAddNewCustomer?: (term: string) => void;
  /** Makes ONLY the SF Opp field editable, independent of onHeaderChange — so it
   *  stays editable even when the rest of the account block is read-only (view
   *  mode). Maps to masterquote.salesforceopportunityid on save. */
  onSfOppChange?: (value: string) => void;
  /** When provided, the Internal Comments box is editable even without the full
   *  onHeaderChange — used by the editor drawer's otherwise read-only account
   *  header to keep just the Internal Comments box editable. */
  onCommentsChange?: (value: string) => void;
  /** Makes ONLY the CSR field an always-editable pick-from-list typeahead,
   *  independent of onHeaderChange — so it stays editable regardless of the
   *  quote's secured/expired status (the assigned CSR can change at any time;
   *  the backend exempts `csr` from the save locks). Twin of onSfOppChange.
   *  Maps to masterquote.csr on save. */
  onCsrChange?: (value: string) => void;
  /** CSR assignment roster ({value: username, label: "NAME (num)"}) backing the
   *  CSR typeahead (GET /cms-lookups/csrs). Only used when onCsrChange is set. A
   *  stored username absent from the roster (e.g. an inactive CSR) is preserved
   *  and flagged rather than dropped. */
  csrOptions?: TypeAheadOption[];
  /** Show the Year-over-Year + Revision Statistics stat grids in the
   *  groupHeader variant (the editor drawer passes this; the grouped page
   *  leaves them hidden — there's no single revision to stat against). */
  showIntelligence?: boolean;
  /**
   * Layout variant (opt-in; default `'full'` keeps the standalone single-quote
   * QuoteDetailPage Customer Info tab UNCHANGED).
   *
   * `'groupHeader'` — rendered as the GROUPED detail page's shared header (and,
   * for the editor drawer, its read-only account header). The group's lanes each
   * carry their OWN per-quote economics. The Bill-To card's "V2 Quote # / Lane"
   * row and the Customer card's "Fuel Calculation" + "Rate Applies" rows are
   * always HIDDEN here (they belong to one lane). The Revenue & Statistics card's
   * per-quote Volume / Est. Revenue $ / Est. Margin % trio shows whenever the
   * surface is NOT `flat`: the editor drawer (not flat) shows it editable via
   * `onEconChange`; the grouped account page (`flat`) hides it and shows an
   * "Average Across All Quotes" roll-up instead. Everything identical across the
   * group (customer/bill-to identity, address, credit, contact) still shows.
   */
  variant?: 'full' | 'groupHeader';
  /** When true (grouped detail page only), the groupHeader trio (Bill To /
   *  Customer / Revenue & Statistics) render WITHOUT their own card chrome —
   *  small headings + fields folded directly into the parent "Account Details"
   *  card. The editor drawer leaves this off and keeps the three cards. */
  flat?: boolean;
  /** Group-level metric averages (grouped detail page) — the mean across the
   *  account's lanes. Renders as an "Average Across All Quotes" block under the
   *  Year-over-Year grid, in the same stat styling. (Volume is omitted — the
   *  per-quote volume field is unreliable; see GroupedQuoteDetailPage.) */
  groupAverages?: { revenue: number | null; margin: number | null };
  /** Whether the Bill To / Customer / Revenue & Statistics / Internal Comments
   *  cards each show their own collapse chevron. Defaults to true (standalone
   *  pages). The editor drawer passes false: it wraps the four in a single
   *  collapsible "Account Details" section instead of per-card toggles. */
  cardsCollapsible?: boolean;
}

const LBL = 110;
// Narrower label for the grouped-header Bill To card's compact 2-column layout
// (fits the longest label, e.g. "Credit Terms", while leaving room for values).
const PAIR_LBL = 84;

/** Fields that become editable inputs when `onHeaderChange` is provided.
 *  Everything else renders as a read-only <FieldValue>. */
const HEADER_EDITABLE: ReadonlySet<keyof QuoteDetailData> = new Set([
  'addr1', 'addr2', 'city', 'stateCode', 'zip', 'billFax',
  'contact', 'phone', 'fax', 'email', 'csr', 'volume',
  'sfOpp', 'other', 'contractBid', 'fuelCalc', 'rateApplies', 'internalComments',
  'estRev', 'estMargin',
]);

const CustomerInfoTab: React.FC<Props> = ({ data, onHeaderChange, onTypeBillTo, onPickBillTo, onTypeCustomer, onPickCustomer, onAddNewBillTo, onAddNewCustomer, onSfOppChange, onCommentsChange, onCsrChange, csrOptions, showIntelligence, variant = 'full', flat = false, groupAverages, cardsCollapsible = true }) => {
  // groupHeader variant hides the per-quote bits (they live in the per-lane
  // editor drawer now). 'full' (default) = standalone behavior, UNCHANGED.
  const isGroupHeader = variant === 'groupHeader';
  // `flat` (grouped detail page) folds the trio into the parent card: render the
  // Bill To / Customer / Revenue sections bare (small heading, no card chrome).
  const bareSections = flat && isGroupHeader;
  // Real Year-over-Year + Revision stats from data.intelligence. Always shown in
  // the full variant; in the grouped header only when the caller opts in
  // (the editor drawer does; the grouped account page leaves them hidden).
  const showIntel = !!data.intelligence && (!isGroupHeader || !!showIntelligence);

  // A field is editable iff the page provided the callback (editable quote) AND
  // the field is in HEADER_EDITABLE. Otherwise it renders as a <FieldValue>.
  const isEditable = (field: keyof QuoteDetailData) =>
    Boolean(onHeaderChange) && HEADER_EDITABLE.has(field);

  // onChange handler for a field — only defined when editing is enabled.
  const ch = (field: keyof QuoteDetailData) =>
    onHeaderChange ? (v: string) => onHeaderChange(field, v) : undefined;

  // CSR field control. When onCsrChange is provided it's an always-editable
  // pick-from-list typeahead (roster = csrOptions; a stored off-roster username
  // is preserved + flagged via flagInvalidValue). Otherwise it falls back to the
  // shared editable/read-only behavior (TextInput when the header is editable,
  // else FieldValue) so surfaces that don't pass onCsrChange are unchanged.
  const csrControl = () =>
    onCsrChange ? (
      <TypeAhead
        options={csrOptions ?? []}
        value={data.csr ?? ''}
        onChange={onCsrChange}
        compact
        showSearch={false}
        flagInvalidValue
        ariaLabel="CSR"
        placeholder="—"
        className="flex-1 min-w-0"
      />
    ) : isEditable('csr') ? (
      <TextInput value={data.csr} onChange={ch('csr')} placeholder="—" />
    ) : (
      <FieldValue value={data.csr} />
    );

  // Key Account is its own full-width line (under Customer / above Market),
  // highlighted with the V1 key-account status color (#51). Falls back to a
  // plain row when there's no key account or no color mapping. `col-span-2` only
  // matters in the groupHeader 2-col grid; it's a harmless no-op in the full
  // single-column layout.
  const kaValue = (data.keyAccount ?? '').trim();
  const kaClass = keyAccountClass(kaValue);
  const keyAccountRow = kaValue && kaClass ? (
    <div className={`col-span-2 rounded my-0.5 px-2.5 py-1.5 flex items-center gap-2 ${kaClass}`}>
      <span className="text-[0.65625rem] font-bold uppercase tracking-wider opacity-80">Key Account</span>
      <span className="text-[0.75rem] font-semibold">{kaValue}</span>
    </div>
  ) : (
    <Row className="col-span-2"><Label width={isGroupHeader ? PAIR_LBL : LBL}>Key Account</Label><FieldValue value={data.keyAccount} /></Row>
  );

  return (
    <div className={isGroupHeader ? 'cih-wrap' : 'contents'}>
      <div className={isGroupHeader ? 'cih-cards' : 'grid grid-cols-3 gap-3'}>
      <Card
        compact={isGroupHeader}
        bare={bareSections}
        collapsible={cardsCollapsible}
        scaleKey="billTo"
        icon={<UserIcon className="h-3.5 w-3.5" />}
        title="Bill To"
        right={data.billToNumber === 0
          ? <span className="text-[0.625rem] font-bold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-px rounded tracking-wider whitespace-nowrap dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">(NEW BILL TO)</span>
          : data.billToNumber
            ? <span className="text-[0.625rem] text-slate-400 dark:text-slate-500">#{data.billToNumber}</span>
            : undefined}
      >
        {isGroupHeader ? (
          /* Compact 2-column layout (grouped header is read-only). Long fields
             (Bill To name, Address, Address 2) span both columns; the short
             ones pair up. State/Zip is shown as a single combined value so it
             fits a half-width cell. */
          <div className="grid grid-cols-2 gap-x-3 items-start">
            <Row className="col-span-2">
              <Label width={PAIR_LBL}>Bill To</Label>
              {onTypeBillTo && onPickBillTo
                ? <BillToLookupField value={data.billTo} linkedCode={data.billToNumber ?? 0} onType={onTypeBillTo} onPick={onPickBillTo} onAddNew={onAddNewBillTo} ariaLabel="Bill To" />
                : <FieldValue value={data.billTo} />}
              {/* Flat layout drops the section header, so the bill-to recno (or
                  NEW BILL TO flag) rides next to the value instead. */}
              {bareSections && (data.billToNumber === 0
                ? <span className="text-[0.625rem] font-bold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-px rounded tracking-wider whitespace-nowrap dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">(NEW BILL TO)</span>
                : data.billToNumber
                  ? <span className="text-[0.625rem] text-slate-400 dark:text-slate-500">#{data.billToNumber}</span>
                  : null)}
            </Row>
            <Row><Label width={PAIR_LBL}>Sage Tag</Label><FieldValue value={data.sageTag} /></Row>
            <Row><Label width={PAIR_LBL}>Bill To Fax</Label>{isEditable('billFax') ? <TextInput value={data.billFax} onChange={ch('billFax')} placeholder="—" /> : <FieldValue value={data.billFax} />}</Row>
            <Row className="col-span-2"><Label width={PAIR_LBL}>Address</Label>{isEditable('addr1') ? <TextInput value={data.addr1} onChange={ch('addr1')} placeholder="—" /> : <FieldValue value={data.addr1} />}</Row>
            <Row className="col-span-2"><Label width={PAIR_LBL}>Address 2</Label>{isEditable('addr2') ? <TextInput value={data.addr2} onChange={ch('addr2')} placeholder="—" /> : <FieldValue value={data.addr2} />}</Row>
            <Row><Label width={PAIR_LBL}>City</Label>{isEditable('city') ? <TextInput value={data.city} onChange={ch('city')} placeholder="—" /> : <FieldValue value={data.city} />}</Row>
            <Row>
              <Label width={PAIR_LBL}>State / Zip</Label>
              {isEditable('stateCode')
                ? <><TextInput value={data.stateCode} onChange={ch('stateCode')} width={44} /><TextInput value={data.zip} onChange={ch('zip')} width={72} /></>
                : <FieldValue value={[data.stateCode, data.zip].filter(Boolean).join(' ')} />}
            </Row>
            <div className="col-span-2"><Divider /></div>
            <Row><Label width={PAIR_LBL}>Credit Terms</Label><FieldValue value={(data.credTerms ?? '').toUpperCase()} /></Row>
            <Row>
              <Label width={PAIR_LBL}>Credit Hold</Label>
              <FieldValue value={data.credHold} width={36} />
              {data.credHold === 'Y' && (
                <span className="text-[0.625rem] font-bold text-red-800 bg-red-100 border border-red-200 px-1.5 py-px rounded tracking-wider dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                  ON HOLD
                </span>
              )}
            </Row>
            <Row><Label width={PAIR_LBL}>A/R Balance</Label><FieldValue value={data.arBalance} /></Row>
          </div>
        ) : (
          <>
            <Row>
              <Label width={LBL}>Bill To</Label>
              {/* Reconcile picker when editable (V1: pick a real bill-to to fill in
                  a deferred billto=0 quote); plain text otherwise. */}
              {onTypeBillTo && onPickBillTo
                ? <BillToLookupField value={data.billTo} linkedCode={data.billToNumber ?? 0} onType={onTypeBillTo} onPick={onPickBillTo} onAddNew={onAddNewBillTo} ariaLabel="Bill To" />
                : <FieldValue value={data.billTo} />}
            </Row>
            <Row><Label width={LBL}>Sage Tag</Label><FieldValue value={data.sageTag} width={120} /></Row>
            <Row><Label width={LBL}>Address</Label>{isEditable('addr1') ? <TextInput value={data.addr1} onChange={ch('addr1')} /> : <FieldValue value={data.addr1} />}</Row>
            <Row><Label width={LBL}>Address 2</Label>{isEditable('addr2') ? <TextInput value={data.addr2} onChange={ch('addr2')} /> : <FieldValue value={data.addr2} />}</Row>
            <Row><Label width={LBL}>City</Label>{isEditable('city') ? <TextInput value={data.city} onChange={ch('city')} /> : <FieldValue value={data.city} />}</Row>
            <Row>
              <Label width={LBL}>State / Zip</Label>
              {isEditable('stateCode') ? <TextInput value={data.stateCode} onChange={ch('stateCode')} width={48} /> : <FieldValue value={data.stateCode} width={48} />}
              {isEditable('zip') ? <TextInput value={data.zip} onChange={ch('zip')} width={96} /> : <FieldValue value={data.zip} width={96} />}
            </Row>
            <Row><Label width={LBL}>Bill To Fax</Label>{isEditable('billFax') ? <TextInput value={data.billFax} onChange={ch('billFax')} placeholder="—" /> : <FieldValue value={data.billFax} />}</Row>

            <Divider />

            <Row><Label width={LBL}>Credit Terms</Label><FieldValue value={(data.credTerms ?? '').toUpperCase()} width={120} /></Row>
            <Row>
              <Label width={LBL}>Credit Hold</Label>
              <FieldValue value={data.credHold} width={56} />
              {data.credHold === 'Y' && (
                <span className="text-[0.625rem] font-bold text-red-800 bg-red-100 border border-red-200 px-1.5 py-px rounded tracking-wider dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                  ON HOLD
                </span>
              )}
            </Row>
            <Row><Label width={LBL}>A/R Balance</Label><FieldValue value={data.arBalance} width={120} /></Row>
          </>
        )}

        <Stack className="mt-1">
          <LabelStacked>Bill To Comments</LabelStacked>
          {/* Scrollable box so long bill-to comments don't push this card past
              the Customer card's height (the cards share a fixed height). */}
          <FieldValue
            value={data.billComments}
            className="!h-auto max-h-[88px] overflow-y-auto min-h-[24px] !items-start !block whitespace-pre-line rounded border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/30 !px-2 !py-1"
          />
        </Stack>

        {/* V2 Quote # / Lane — a PER-QUOTE cross-reference, so hidden in the
            grouped shared header (the group spans many quotes). */}
        {!isGroupHeader && (
          <>
            <Divider />
            <Row>
              <Label width={LBL}>V2 Quote # / Lane</Label>
              {/* #52: legacy v2 cross-reference, display-only (no V2 system to link
                  to from FastCMS — the dead "View" link is removed). */}
              <FieldValue value={data.v2QuoteNumber ? String(data.v2QuoteNumber) : ''} width={80} />
              <FieldValue value={data.v2Lane} />
            </Row>
          </>
        )}
      </Card>

      <Card compact={isGroupHeader} bare={bareSections} collapsible={cardsCollapsible} scaleKey="customer" icon={<UserIcon className="h-3.5 w-3.5" />} title="Customer" right={data.customerCode === 0
        ? <span className="text-[0.625rem] font-bold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-px rounded tracking-wider whitespace-nowrap dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">(NEW CUSTOMER)</span>
        : data.customerCode
          ? <span className="text-[0.625rem] text-slate-400 dark:text-slate-500">#{data.customerCode}</span>
          : undefined}>
        {isGroupHeader ? (
          /* Compact 2-column layout (grouped header is read-only); mirrors the
             Bill To card. Long-value + Salesforce fields span both columns; the
             short ones pair up. "Salesforce"/"Contract/Bid #" are abbreviated so
             every label fits the narrow PAIR_LBL column. */
          <div className="grid grid-cols-2 gap-x-3 items-start">
            <Row className="col-span-2">
              <Label width={PAIR_LBL}>Customer</Label>
              {onTypeCustomer && onPickCustomer
                ? <CustomerLookupField value={data.customer} linkedCode={data.customerCode ?? 0} onType={onTypeCustomer} onPick={onPickCustomer} onAddNew={onAddNewCustomer} ariaLabel="Customer" />
                : <FieldValue value={data.customer} />}
              {/* Flat layout: customer recno (or NEW CUSTOMER when unlinked,
                  customercode 0) rides next to the value — mirrors Bill To. */}
              {bareSections && (data.customerCode === 0
                ? <span className="text-[0.625rem] font-bold text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-px rounded tracking-wider whitespace-nowrap dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">(NEW CUSTOMER)</span>
                : data.customerCode
                  ? <span className="text-[0.625rem] text-slate-400 dark:text-slate-500">#{data.customerCode}</span>
                  : null)}
            </Row>
            {keyAccountRow}
            <Row className="col-span-2"><Label width={PAIR_LBL}>Market</Label><FieldValue value={data.market} /></Row>
            <div className="col-span-2"><Divider /></div>
            <Row><Label width={PAIR_LBL}>Contact</Label>{isEditable('contact') ? <TextInput value={data.contact} onChange={ch('contact')} placeholder="—" /> : <FieldValue value={data.contact} />}</Row>
            <Row><Label width={PAIR_LBL}>Phone</Label>{isEditable('phone') ? <TextInput value={data.phone} onChange={ch('phone')} placeholder="—" /> : <FieldValue value={data.phone} />}</Row>
            <Row><Label width={PAIR_LBL}>Fax</Label>{isEditable('fax') ? <TextInput value={data.fax} onChange={ch('fax')} placeholder="—" /> : <FieldValue value={data.fax} />}</Row>
            <Row><Label width={PAIR_LBL}>Other</Label>{isEditable('other') ? <TextInput value={data.other} onChange={ch('other')} placeholder="—" /> : <FieldValue value={data.other} />}</Row>
            <Row className="min-w-0"><Label width={PAIR_LBL}>Email</Label>{isEditable('email') ? <TextInput value={data.email} onChange={ch('email')} placeholder="—" /> : <FieldValue value={data.email} truncate />}</Row>
            <Row><Label width={PAIR_LBL}>SF Opp</Label>{(onSfOppChange || isEditable('sfOpp')) ? <TextInput value={data.sfOpp} onChange={onSfOppChange ?? ch('sfOpp')} placeholder="—" /> : <FieldValue value={data.sfOpp} />}</Row>
            <div className="col-span-2"><Divider /></div>
            <Row className="col-span-2"><Label width={PAIR_LBL}>Contract #</Label>{isEditable('contractBid') ? <TextInput value={data.contractBid} onChange={ch('contractBid')} placeholder="—" /> : <FieldValue value={data.contractBid} />}</Row>
            <Row><Label width={PAIR_LBL}>CSR</Label>{csrControl()}</Row>
          </div>
        ) : (
          <>
            <Row><Label width={LBL}>Customer</Label><FieldValue value={data.customer} /></Row>
            {keyAccountRow}
            <Row><Label width={LBL}>Market</Label><FieldValue value={data.market} /></Row>
            <Row><Label width={LBL}>Salesforce Opp</Label>{(onSfOppChange || isEditable('sfOpp')) ? <TextInput value={data.sfOpp} onChange={onSfOppChange ?? ch('sfOpp')} placeholder="—" /> : <FieldValue value={data.sfOpp} />}</Row>

            <Divider />

            <Row><Label width={LBL}>Contact</Label>{isEditable('contact') ? <TextInput value={data.contact} onChange={ch('contact')} placeholder="—" /> : <FieldValue value={data.contact} />}</Row>
            <Row><Label width={LBL}>Phone</Label>{isEditable('phone') ? <TextInput value={data.phone} onChange={ch('phone')} placeholder="(   )   -    " /> : <FieldValue value={data.phone} />}</Row>
            <Row><Label width={LBL}>Fax</Label>{isEditable('fax') ? <TextInput value={data.fax} onChange={ch('fax')} placeholder="—" /> : <FieldValue value={data.fax} />}</Row>
            <Row className="min-w-0"><Label width={LBL}>Email</Label>{isEditable('email') ? <TextInput value={data.email} onChange={ch('email')} placeholder="—" /> : <FieldValue value={data.email} truncate />}</Row>
            <Row><Label width={LBL}>Other</Label>{isEditable('other') ? <TextInput value={data.other} onChange={ch('other')} placeholder="—" /> : <FieldValue value={data.other} />}</Row>

            <Divider />

            <Row><Label width={LBL}>Contract/Bid #</Label>{isEditable('contractBid') ? <TextInput value={data.contractBid} onChange={ch('contractBid')} placeholder="—" /> : <FieldValue value={data.contractBid} />}</Row>
            {/* CSR is a short username in V1 (sourced from gwi.users). The editor
                drawer passes onCsrChange + csrOptions to render a pick-from-list
                typeahead (GET /cms-lookups/csrs); this full-page variant falls
                back to free text / read-only. */}
            <Row><Label width={LBL}>CSR</Label>{csrControl()}</Row>
            {/* Fuel Calculation + Rate Applies are PER-QUOTE settings — hidden in the
                grouped shared header (each lane sets its own in the editor drawer). */}
            {/* Both are required to save, so a blank flags itself red here too
                (same cue the lane drawer gives in its Drayage & Fuel column). */}
            <Row><Label width={LBL}>Fuel Calculation</Label>{isEditable('fuelCalc') ? <SelectInput value={data.fuelCalc} onChange={ch('fuelCalc')} options={withCurrent(data.fuelCalc, FUEL_CALC_TYPES)} ariaLabel="Fuel Calculation" highlight={(data.fuelCalc ?? '').trim() ? undefined : 'red'} /> : <FieldValue value={data.fuelCalc} />}</Row>
            <Row><Label width={LBL}>Rate Applies</Label>{isEditable('rateApplies') ? <SelectInput value={data.rateApplies} onChange={ch('rateApplies')} options={withCurrent(data.rateApplies, RATE_APPLIES)} ariaLabel="Rate Applies" highlight={(data.rateApplies ?? '').trim() ? undefined : 'red'} /> : <FieldValue value={data.rateApplies} />}</Row>
          </>
        )}
      </Card>

      {/* Column 3: Revenue & Statistics stacked above Internal Comments so the
          two share the third column. */}
      <div className="flex flex-col gap-2 min-w-0">
      <Card compact={isGroupHeader} bare={bareSections} collapsible={cardsCollapsible} scaleKey="revenueStats" icon={<CubeIcon className="h-3.5 w-3.5" />} title="Revenue & Statistics">
        {/* The per-quote Volume / Est. Revenue $ / Est. Margin % trio now lives in
            the Quote Details header's "Revenue" column (see QuoteDetailHeader).
            This card carries the Year-over-Year + Revision Statistics roll-ups. */}

        {/* Year-over-Year + Revision Statistics are PER-QUOTE stat grids — hidden
            in the grouped shared header (no single revision to stat against). */}
        {showIntel && data.intelligence && (
          <>
            {/* Year over Year is the first thing in this card now (the per-quote
                trio moved to the Quote Details header), so there's no leading
                divider — it would read as a stray rule under the card header. */}
            <div className="text-[0.625rem] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">Year over Year</div>
            <div className="grid grid-cols-[90px_1fr_1fr] gap-x-2 gap-y-1 items-center">
              <div></div>
              <div className="text-[0.65625rem] font-semibold text-slate-500 dark:text-slate-400 uppercase text-left">Previous Year</div>
              <div className="text-[0.65625rem] font-semibold text-slate-900 dark:text-slate-100 uppercase text-left">Current Year</div>
              <Label>Volume</Label>
              <FieldValue value={fmtIntelVol(data.intelligence.prevYear.volume)} />
              <FieldValue value={fmtIntelVol(data.intelligence.currentYear.volume)} />
              <Label>Revenue</Label>
              <FieldValue value={fmtIntelRev(data.intelligence.prevYear.revenue)} />
              <FieldValue value={fmtIntelRev(data.intelligence.currentYear.revenue)} />
              <Label>Margin %</Label>
              <FieldValue value={fmtIntelMargin(data.intelligence.prevYear.margin)} />
              <FieldValue value={fmtIntelMargin(data.intelligence.currentYear.margin)} />
            </div>

            {/* Revision Statistics moved to the Quote Details header (next to the
                Revenue column) so the rep's estimate sits beside the actuals. */}
          </>
        )}

        {/* Group-level roll-up — the mean of each metric across the account's
            lanes. Sits under Year over Year with the same stat styling. */}
        {groupAverages && (
          <>
            {showIntel && data.intelligence && <Divider tall />}
            <div className="text-[0.625rem] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5">Average Across All Quotes</div>
            {/* Volume is intentionally omitted — the per-quote quoterevisions.volume
                field is left at 1 on most quotes, so its average is meaningless. */}
            <Row><Label width={LBL}>Revenue</Label><FieldValue value={groupAverages.revenue == null ? '—' : fmtIntelRev(groupAverages.revenue)} /></Row>
            <Row><Label width={LBL}>Margin %</Label><FieldValue value={fmtIntelMargin(groupAverages.margin)} /></Row>
          </>
        )}
      </Card>

      {/* Internal Comments — per-quote masterquote.mastercomments. Sits below
          Revenue & Statistics, sharing the third column. Per-quote, so hidden on
          the grouped (flat) page. */}
      {!flat && (
        <Card compact={isGroupHeader} fillBody collapsible={cardsCollapsible} scaleKey="internalComments" className="flex-1" icon={<ChatBubbleLeftIcon className="h-3.5 w-3.5" />} title="Internal Comments">
          {(isEditable('internalComments') || onCommentsChange) ? (
            <textarea
              value={data.internalComments}
              onChange={(e) => (onCommentsChange ?? ((v: string) => onHeaderChange!('internalComments', v)))(e.target.value)}
              placeholder="Add internal notes about this quote…"
              className="w-full flex-1 min-h-[72px] border border-slate-300 rounded px-2 py-1.5 text-[0.75rem] text-slate-900 resize-y bg-amber-50 font-sans dark:border-slate-600 dark:text-slate-100 dark:bg-amber-900/20"
            />
          ) : (
            <FieldValue value={data.internalComments} placeholder="Add internal notes about this quote…" className="!h-auto flex-1 min-h-[24px] !items-start !block" />
          )}
        </Card>
      )}
      </div>
      </div>
    </div>
  );
};

const fmtIntelVol = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtIntelRev = (v: number) => `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtIntelMargin = (v: number | null) => (v == null ? '—' : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const Divider: React.FC<{ tall?: boolean }> = ({ tall }) => (
  <div className={`h-px bg-slate-100 dark:bg-slate-700 ${tall ? 'my-2' : 'my-1.5'}`} />
);

export default CustomerInfoTab;
